import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, X,
  Loader2, CheckCircle, Ban, Clock, Globe, Repeat, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const isSameDay = (a, b) => toDateStr(a) === toDateStr(b);

const DEFAULT_SLOTS = [
  { start: '09:00', end: '10:00' },
  { start: '10:00', end: '11:00' },
  { start: '11:00', end: '12:00' },
  { start: '14:00', end: '15:00' },
  { start: '15:00', end: '16:00' },
  { start: '16:00', end: '17:00' },
];

// Common IANA timezone options relevant for Brazil / global use
const TZ_OPTIONS = [
  { value: 'America/Sao_Paulo',   label: 'Brasília (UTC-3)' },
  { value: 'America/Manaus',      label: 'Manaus (UTC-4)' },
  { value: 'America/Belem',       label: 'Belém (UTC-3)' },
  { value: 'America/Fortaleza',   label: 'Fortaleza (UTC-3)' },
  { value: 'America/New_York',    label: 'New York (ET)' },
  { value: 'America/Chicago',     label: 'Chicago (CT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'Europe/London',       label: 'London (GMT/BST)' },
  { value: 'Europe/Lisbon',       label: 'Lisbon (WET/WEST)' },
  { value: 'UTC',                 label: 'UTC' },
];

export const AdminAvailability = () => {
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  // Teachers
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(true);

  // Calendar
  const [weekOffset, setWeekOffset] = useState(0);
  const [availability, setAvailability] = useState({});
  const [classes, setClasses] = useState({});
  const [loading, setLoading] = useState(true);

  // Day edit modal
  const [editDate, setEditDate]   = useState(null);
  const [editSlots, setEditSlots] = useState([]);
  const [editBlocked, setEditBlocked] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Weekly template modal
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSlots, setTemplateSlots] = useState([]); // [{weekday, start, end}]
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingTz, setSavingTz] = useState(false);
  const [editTz, setEditTz] = useState('America/Sao_Paulo');

  // ── Fetch teachers once on mount ─────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/teachers?page_size=200`)
      .then(res => {
        const teachersList = res.data.data ?? res.data;
        setTeachers(teachersList);
        if (teachersList.length > 0) {
          const first = teachersList[0];
          setSelectedTeacherId(first.id);
          const profile = first.profile || {};
          setTeacherProfile(profile);
          setEditTz(profile.timezone || 'America/Sao_Paulo');
          setTemplateSlots(profile.weekly_template || []);
        }
      })
      .catch(() => toast.error('Failed to load teachers'))
      .finally(() => setLoadingTeachers(false));
  }, []);

  const handleTeacherChange = async (tid) => {
    setSelectedTeacherId(tid);
    try {
      const res = await axios.get(`${API_URL}/api/admin/teachers/${tid}/profile`);
      setTeacherProfile(res.data);
      setEditTz(res.data.timezone || 'America/Sao_Paulo');
      setTemplateSlots(res.data.weekly_template || []);
    } catch {
      setTeacherProfile(null);
    }
  };

  // ── Calendar data fetch ───────────────────────────────────────────────────────
  const getWeekStart = useCallback(() => {
    const d = new Date(today);
    const dayOfWeek = d.getDay();
    d.setDate(d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1) + weekOffset * 7);
    return d;
  }, [weekOffset, today]);

  const fetchData = useCallback(async () => {
    if (!selectedTeacherId) return;
    setLoading(true);
    const start = getWeekStart();
    const end   = addDays(start, 34);
    try {
      const [availRes, calRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/availability?teacher_id=${selectedTeacherId}&start=${toDateStr(start)}&end=${toDateStr(end)}`),
        axios.get(`${API_URL}/api/admin/calendar?teacher_id=${selectedTeacherId}&start=${toDateStr(start)}&end=${toDateStr(end)}`),
      ]);
      const avMap = {};
      for (const rec of availRes.data) avMap[rec.date] = rec;
      setAvailability(avMap);

      const clMap = {};
      for (const cls of calRes.data) {
        if (!cls.scheduled_at) continue;
        const dateKey = cls.scheduled_at.slice(0, 10);
        if (!clMap[dateKey]) clMap[dateKey] = [];
        clMap[dateKey].push(cls);
      }
      setClasses(clMap);
    } catch {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  }, [getWeekStart, selectedTeacherId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Day edit helpers ──────────────────────────────────────────────────────────
  const openEdit = (dateStr) => {
    const rec = availability[dateStr];
    setEditDate(dateStr);
    setEditSlots(rec?.slots ? rec.slots.map(s => ({ ...s })) : []);
    setEditBlocked(rec?.is_blocked || false);
    setEditNotes(rec?.notes || '');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/availability/${editDate}?teacher_id=${selectedTeacherId}`,
        { slots: editSlots, is_blocked: editBlocked, notes: editNotes }
      );
      setAvailability(prev => ({ ...prev, [editDate]: res.data }));
      toast.success('Availability saved');
      setEditDate(null);
    } catch {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/availability/${editDate}?teacher_id=${selectedTeacherId}`);
      setAvailability(prev => { const n = { ...prev }; delete n[editDate]; return n; });
      toast.success('Availability cleared');
      setEditDate(null);
    } catch {
      toast.error('Failed to clear availability');
    } finally {
      setDeleting(false);
    }
  };

  const addSlot = () => setEditSlots(prev => [...prev, { start: '09:00', end: '10:00' }]);
  const removeSlot = (i) => setEditSlots(prev => prev.filter((_, idx) => idx !== i));
  const updateSlot = (i, field, val) =>
    setEditSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  // ── Weekly template helpers ───────────────────────────────────────────────────
  const addTemplateSlot = (weekday) => {
    setTemplateSlots(prev => [...prev, { weekday, start: '09:00', end: '10:00' }]);
  };
  const removeTemplateSlot = (i) => setTemplateSlots(prev => prev.filter((_, idx) => idx !== i));
  const updateTemplateSlot = (i, field, val) =>
    setTemplateSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/teachers/${selectedTeacherId}/profile`, {
        weekly_template: templateSlots,
      });
      setTeacherProfile(res.data);
      toast.success('Weekly template saved');
      setTemplateOpen(false);
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSaveTimezone = async () => {
    setSavingTz(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/teachers/${selectedTeacherId}/profile`, {
        timezone: editTz,
      });
      setTeacherProfile(res.data);
      toast.success('Timezone saved');
    } catch {
      toast.error('Failed to save timezone');
    } finally {
      setSavingTz(false);
    }
  };

  // Apply weekly template to currently visible week dates that have no config yet
  const handleApplyTemplate = async () => {
    if (!templateSlots.length) { toast.error('No template slots configured'); return; }
    const weekStart = getWeekStart();
    const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    let applied = 0;
    for (const d of dates) {
      const dateStr = toDateStr(d);
      if (availability[dateStr]) continue; // already has config — skip
      // weekday: d.getDay() → 0=Sun, need ISO Mon=0
      const isoWeekday = (d.getDay() + 6) % 7; // Mon=0…Sun=6
      const daySlots = templateSlots.filter(s => s.weekday === isoWeekday).map(s => ({ start: s.start, end: s.end }));
      if (!daySlots.length) continue;
      try {
        const res = await axios.put(
          `${API_URL}/api/admin/availability/${dateStr}?teacher_id=${selectedTeacherId}`,
          { slots: daySlots, is_blocked: false, notes: '' }
        );
        setAvailability(prev => ({ ...prev, [dateStr]: res.data }));
        applied++;
      } catch {
        // skip silently
      }
    }
    if (applied > 0) toast.success(`Template applied to ${applied} day${applied > 1 ? 's' : ''}`);
    else toast.info('All days already have configuration');
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const weekStart = getWeekStart();
  const weeks = Array.from({ length: 5 }, (_, wi) =>
    Array.from({ length: 7 }, (_, di) => addDays(weekStart, wi * 7 + di))
  );

  const weekRangeLabel = () => {
    const s = weekStart;
    const e = addDays(weekStart, 34);
    return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
  };

  if (loadingTeachers) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-availability-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Availability</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Set available teaching slots per teacher</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setTemplateOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-[#1A2E6E]/20 text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium"
          >
            <Repeat size={15} /> Weekly Template
          </button>
          <button
            onClick={handleApplyTemplate}
            disabled={!templateSlots.length}
            title="Apply template to unset days in the current week"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] disabled:opacity-40 transition-colors text-sm font-medium"
          >
            <CheckCircle size={15} /> Apply to Week
          </button>
        </div>
      </div>

      {/* Teacher selector + timezone strip */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] p-4 flex flex-wrap items-end gap-4">
        {teachers.length > 1 && (
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-[#1A2E6E]/60 mb-1">Teacher</label>
            <select
              value={selectedTeacherId}
              onChange={e => handleTeacherChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white text-sm"
            >
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-[#1A2E6E]/60 mb-1 flex items-center gap-1">
            <Globe size={12} /> Timezone
          </label>
          <div className="flex gap-2">
            <select
              value={editTz}
              onChange={e => setEditTz(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white text-sm"
            >
              {TZ_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <button
              onClick={handleSaveTimezone}
              disabled={savingTz || editTz === teacherProfile?.timezone}
              className="px-3 py-2 rounded-lg bg-[#1A2E6E] text-white text-sm font-medium hover:bg-[#16275C] disabled:opacity-40 transition-colors flex items-center gap-1"
            >
              {savingTz ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Save
            </button>
          </div>
        </div>
        {teacherProfile?.booking_buffer_minutes > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-[#1A2E6E]/50 pb-2">
            <Info size={12} />
            {teacherProfile.booking_buffer_minutes}min buffer between classes
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setWeekOffset(w => Math.max(w - 1, 0))}
          disabled={weekOffset === 0}
          className="p-2 rounded-lg border border-[#E8EAF0] hover:bg-[#F5F7FA] disabled:opacity-30 transition-colors"
        >
          <ChevronLeft size={18} className="text-[#1A2E6E]" />
        </button>
        <span className="text-sm font-medium text-[#1A2E6E] min-w-[220px] text-center">{weekRangeLabel()}</span>
        <button
          onClick={() => setWeekOffset(w => Math.min(w + 1, 3))}
          disabled={weekOffset >= 3}
          className="p-2 rounded-lg border border-[#E8EAF0] hover:bg-[#F5F7FA] disabled:opacity-30 transition-colors"
        >
          <ChevronRight size={18} className="text-[#1A2E6E]" />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-[#1A2E6E]/60">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#4CAF50]/20 border border-[#4CAF50]/40 inline-block" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" /> Blocked</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#1A2E6E]/10 border border-[#1A2E6E]/20 inline-block" /> Class scheduled</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-[#E8EAF0] inline-block" /> No config</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-x-auto">
          <div className="grid grid-cols-7 border-b border-[#E8EAF0]">
            {DAYS.map(d => (
              <div key={d} className="py-3 text-center text-xs font-semibold text-[#1A2E6E]/50 uppercase tracking-wide">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-[#E8EAF0] last:border-b-0">
              {week.map((day) => {
                const dateStr = toDateStr(day);
                const rec     = availability[dateStr];
                const dayCls  = classes[dateStr] || [];
                const isPast  = day < today;
                const isToday = isSameDay(day, today);

                let bgClass = 'bg-white hover:bg-[#F5F7FA]';
                if (rec?.is_blocked) bgClass = 'bg-red-50 hover:bg-red-100';
                else if (rec?.slots?.length > 0) bgClass = 'bg-[#4CAF50]/5 hover:bg-[#4CAF50]/10';
                if (isPast) bgClass = 'bg-[#F9FAFB] opacity-60 cursor-default';

                return (
                  <motion.button
                    key={dateStr}
                    onClick={() => !isPast && openEdit(dateStr)}
                    disabled={isPast}
                    whileHover={!isPast ? { scale: 1.01 } : {}}
                    className={`p-2 min-h-[80px] text-left border-r border-[#E8EAF0] last:border-r-0 transition-all ${bgClass} ${!isPast ? 'cursor-pointer' : ''}`}
                    data-testid={`avail-day-${dateStr}`}
                  >
                    <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-[#D72B2B]' : 'text-[#1A2E6E]'}`}>
                      {isToday && <span className="inline-flex w-5 h-5 rounded-full bg-[#D72B2B] text-white text-xs items-center justify-center mr-1">{day.getDate()}</span>}
                      {!isToday && day.getDate()}
                      {day.getDate() === 1 && <span className="text-xs font-normal text-[#1A2E6E]/40 ml-1">{MONTHS[day.getMonth()].slice(0,3)}</span>}
                    </div>
                    {rec?.is_blocked && (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs"><Ban size={10} /> Blocked</span>
                    )}
                    {!rec?.is_blocked && rec?.slots?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[#4CAF50] text-xs"><Clock size={10} /> {rec.slots.length} slot{rec.slots.length > 1 ? 's' : ''}</span>
                    )}
                    {dayCls.length > 0 && (
                      <div className="mt-1">
                        {dayCls.slice(0, 2).map((cls, i) => (
                          <span key={i} className="block text-[10px] bg-[#1A2E6E]/10 text-[#1A2E6E] rounded px-1 mb-0.5 truncate">
                            {cls.scheduled_at?.slice(11, 16)} {cls.title || 'Class'}
                          </span>
                        ))}
                        {dayCls.length > 2 && <span className="text-[10px] text-[#1A2E6E]/40">+{dayCls.length - 2} more</span>}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Day Edit Modal */}
      <AnimatePresence>
        {editDate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Edit Availability</h2>
                  <p className="text-sm text-[#1A2E6E]/60">
                    {new Date(editDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    {teacherProfile?.timezone && (
                      <span className="ml-2 text-[#1A2E6E]/40">· {teacherProfile.timezone}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setEditDate(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-5">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => setEditBlocked(b => !b)}
                    className={`w-11 h-6 rounded-full transition-colors ${editBlocked ? 'bg-red-500' : 'bg-[#E8EAF0]'}`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow m-0.5 transition-transform ${editBlocked ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className="font-medium text-[#1A2E6E] text-sm">Mark entire day as blocked</span>
                </label>

                {!editBlocked && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-semibold text-[#1A2E6E] text-sm">Available Time Slots</p>
                      <button onClick={addSlot} className="flex items-center gap-1 text-xs text-[#D72B2B] hover:text-[#B71C1C] font-medium">
                        <Plus size={14} /> Add Slot
                      </button>
                    </div>
                    {editSlots.length === 0 && (
                      <p className="text-sm text-[#1A2E6E]/40 italic">No slots — click "Add Slot" to add available times.</p>
                    )}
                    <div className="space-y-2">
                      {editSlots.map((slot, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input type="time" value={slot.start} onChange={e => updateSlot(i, 'start', e.target.value)}
                            className="flex-1 px-3 py-2 rounded-lg border border-[#E8EAF0] text-sm focus:border-[#1A2E6E] outline-none" />
                          <span className="text-[#1A2E6E]/40 text-xs">to</span>
                          <input type="time" value={slot.end} onChange={e => updateSlot(i, 'end', e.target.value)}
                            className="flex-1 px-3 py-2 rounded-lg border border-[#E8EAF0] text-sm focus:border-[#1A2E6E] outline-none" />
                          <button onClick={() => removeSlot(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    {editSlots.length === 0 && (
                      <button onClick={() => setEditSlots(DEFAULT_SLOTS.map(s => ({ ...s })))}
                        className="mt-3 text-xs text-[#1A2E6E]/50 hover:text-[#1A2E6E] underline">
                        Add standard day (9am–12pm, 2pm–5pm)
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Notes (optional)</label>
                  <textarea
                    value={editNotes}
                    onChange={e => setEditNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. Holiday, different schedule..."
                    className="w-full px-3 py-2 rounded-lg border border-[#E8EAF0] text-sm focus:border-[#1A2E6E] outline-none resize-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-6 border-t border-[#E8EAF0] gap-3">
                <button onClick={handleDelete} disabled={deleting || saving}
                  className="text-sm text-red-500 hover:text-red-700 font-medium disabled:opacity-50">
                  {deleting ? <Loader2 size={16} className="animate-spin inline" /> : 'Clear Day'}
                </button>
                <div className="flex gap-3">
                  <button onClick={() => setEditDate(null)}
                    className="px-4 py-2 rounded-xl border border-[#E8EAF0] text-sm font-medium text-[#1A2E6E]/70 hover:bg-[#F5F7FA]">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving || deleting}
                    className="px-5 py-2 rounded-xl bg-[#1A2E6E] text-white text-sm font-medium hover:bg-[#2A4A8E] disabled:opacity-50 flex items-center gap-2">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Weekly Template Modal */}
      <AnimatePresence>
        {templateOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Weekly Template</h2>
                  <p className="text-sm text-[#1A2E6E]/60">
                    Recurring slots auto-applied with "Apply to Week". Doesn't overwrite existing days.
                  </p>
                </div>
                <button onClick={() => setTemplateOpen(false)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {WEEKDAYS_LONG.map((dayName, weekday) => {
                  const daySlots = templateSlots.filter(s => s.weekday === weekday);
                  return (
                    <div key={weekday}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-[#1A2E6E]">{dayName}</p>
                        <button onClick={() => addTemplateSlot(weekday)}
                          className="flex items-center gap-1 text-xs text-[#D72B2B] hover:text-[#B71C1C] font-medium">
                          <Plus size={12} /> Add
                        </button>
                      </div>
                      {daySlots.length === 0 && (
                        <p className="text-xs text-[#1A2E6E]/30 italic ml-1">No slots (day off)</p>
                      )}
                      <div className="space-y-1.5">
                        {templateSlots.map((slot, i) => {
                          if (slot.weekday !== weekday) return null;
                          return (
                            <div key={i} className="flex items-center gap-2">
                              <input type="time" value={slot.start}
                                onChange={e => updateTemplateSlot(i, 'start', e.target.value)}
                                className="flex-1 px-2 py-1.5 rounded-lg border border-[#E8EAF0] text-sm focus:border-[#1A2E6E] outline-none" />
                              <span className="text-[#1A2E6E]/40 text-xs">to</span>
                              <input type="time" value={slot.end}
                                onChange={e => updateTemplateSlot(i, 'end', e.target.value)}
                                className="flex-1 px-2 py-1.5 rounded-lg border border-[#E8EAF0] text-sm focus:border-[#1A2E6E] outline-none" />
                              <button onClick={() => removeTemplateSlot(i)}
                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
                <button onClick={() => setTemplateOpen(false)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-sm font-medium text-[#1A2E6E]/70 hover:bg-[#F5F7FA]">
                  Cancel
                </button>
                <button onClick={handleSaveTemplate} disabled={savingTemplate}
                  className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white text-sm font-medium hover:bg-[#16275C] disabled:opacity-50 flex items-center justify-center gap-2">
                  {savingTemplate ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Save Template
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAvailability;
